import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import BZAPIService from '../../../services/BZAPIService';
import Select from '../../common/Select';
import { isEmpty } from 'lodash';
import {
  AreasTable,
  DEGRADED_CALLOUT,
  RUN_STATUSES,
  RunPill,
  STOPPED_CALLOUT,
  runStatusKey,
  runStatusLabel,
  sortAreas,
  worstArea
} from '../../bz/runHealth';

// Format date to YYYY-MM-DD HH:mm:ss
const formatDate = dateString => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// The numeric columns after Ended, Health and Errors. "Sync only" is a pill
// beside the Ended time (STRY0038849), so the table fits 1280 px.
const COUNT_COLUMNS = [
  { key: 'compatibleCis', label: 'Compat­ible' },
  { key: 'incompatibleCis', label: 'Incompat­ible' },
  { key: 'bugsFiltered', label: 'Bugs filtered' },
  { key: 'bugsInserted', label: 'Bugs inserted' },
  { key: 'discoveredManagedProducts', label: 'Managed products' }
];
const COLUMN_COUNT = 4 + COUNT_COLUMNS.length;

// Filter options for the dropdown
const FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Bugs inserted', value: 'bugsinserted' },
  { label: 'Bugs updated', value: 'bugsupdated' },
  { label: 'Errors', value: 'errors' }
];

// How many runs the health strip shows.
const STRIP_RUNS = 30;

const onlyConfigured = res => res?.data?.results?.filter(h => h?.vendorData?.configuration_completed === true) || [];

// The run's health, when the run checked its requirements (null before STRY0038849).
const healthOf = entry => {
  const health = entry?.vendorData?.health;
  return health && typeof health === 'object' ? health : null;
};

const countValue = value => (value === null || value === undefined || value === 0 ? '-' : value);

const firstError = entry => {
  const errors = entry?.vendorData?.errors;
  if (!Array.isArray(errors) || errors.length === 0) return null;
  return errors[0]?.value || 'Error occurred';
};

const STRIP_TONES = { operational: 'ok', degraded: 'warn', stopped: 'bad', retrying: 'info' };
const STRIP_LEGEND = [
  ['operational', 'Passed'],
  ['degraded', 'Degraded'],
  ['stopped', 'Stopped at health check'],
  ['retrying', 'Retrying']
];

const stripKey = entry => {
  const health = healthOf(entry);
  return health ? runStatusKey(health.status) : null;
};

const stripLabel = key => (key && key !== 'none' ? runStatusLabel(key, { history: true }) : 'Not checked');

// "30 runs: 26 passed, 2 degraded, 1 stopped at health check, 1 not checked"
const stripSummary = runs => {
  const counts = runs.reduce((acc, r) => {
    const key = stripKey(r) || 'unchecked';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const parts = [
    ...STRIP_LEGEND.map(([key, label]) => [counts[key], label.toLowerCase()]),
    [(counts.none || 0) + (counts.unchecked || 0), 'not checked']
  ]
    .filter(([n]) => n)
    .map(([n, label]) => `${n} ${label}`);
  return `${runs.length} ${runs.length === 1 ? 'run' : 'runs'}: ${parts.join(', ')}`;
};

const stripDay = iso => {
  const m = iso ? moment(iso) : null;
  return m && m.isValid() ? m.format('MMM D') : null;
};

// One square per run, oldest on the left. Shown only when at least one of the
// runs checked its requirements.
const HealthStrip = ({ runs }) => {
  const first = stripDay(runs[0]?.endedAt);
  const last = stripDay(runs[runs.length - 1]?.endedAt);
  const hasUnchecked = runs.some(r => !stripKey(r) || stripKey(r) === 'none');
  return (
    <div
      className="bz-card"
      role="region"
      aria-label="Run health"
      style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div className="bz-row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h3 className="bz-h3">
          Run health · last {runs.length} {runs.length === 1 ? 'run' : 'runs'}
        </h3>
        <span className="bz-muted" style={{ fontSize: 'var(--fs-sm)' }}>
          {first && last ? `${first} → ${last} · ` : ''}one square per run
        </span>
      </div>
      <div className="rh-strip" role="img" aria-label={stripSummary(runs)} data-testid="run-health-strip">
        {runs.map((r, i) => {
          const key = stripKey(r);
          const when = r.endedAt && moment(r.endedAt).isValid() ? moment(r.endedAt).format('MMM D, HH:mm') : '';
          return (
            <span
              key={r.id || i}
              className={`rh-q rh-q--${STRIP_TONES[key] || 'none'}`}
              title={`${when}${when ? ': ' : ''}${stripLabel(key)}`}
              data-status={key || 'none'}
            />
          );
        })}
      </div>
      <div className="rh-strip-legend">
        {STRIP_LEGEND.map(([key, label]) => (
          <span key={key}>
            <span className={`rh-q rh-q--${STRIP_TONES[key]}`} aria-hidden="true" />
            {label}
          </span>
        ))}
        {hasUnchecked && (
          <span>
            <span className="rh-q rh-q--none" aria-hidden="true" />
            Not checked
          </span>
        )}
      </div>
    </div>
  );
};
HealthStrip.propTypes = { runs: PropTypes.arrayOf(PropTypes.object).isRequired };

const Chevron = ({ open }) => (
  <svg
    className={`rh-chev${open ? ' is-open' : ''}`}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);
Chevron.propTypes = { open: PropTypes.bool };

const HealthCell = ({ health }) => {
  if (!health) {
    return (
      <span className="rh-na" title="No requirement checks recorded for this run">
        —
      </span>
    );
  }
  const worst = worstArea(health.areas);
  const why = worst ? `${worst.label}: ${worst.message}` : null;
  return (
    <>
      <RunPill status={health.status} history />
      {why && (
        <span className="rh-history__why" title={why}>
          {why}
        </span>
      )}
    </>
  );
};
HealthCell.propTypes = { health: PropTypes.object };

const RunDetail = ({ health, endedAt, id }) => {
  const key = runStatusKey(health.status);
  const callout = key === 'stopped' ? STOPPED_CALLOUT : key === 'degraded' ? DEGRADED_CALLOUT : null;
  return (
    <div id={id}>
      {sortAreas(health.areas).length > 0 ? (
        <div className="rh-history__dbox">
          <AreasTable areas={health.areas} runIso={endedAt} label="What this run checked" outcomeFirst />
        </div>
      ) : (
        <div className="bz-muted" style={{ fontSize: 'var(--fs-sm)' }}>
          This run has no requirement results.
        </div>
      )}
      {callout && <div className={`rh-callout rh-callout--${RUN_STATUSES[key].tone}`}>{callout}</div>}
    </div>
  );
};
RunDetail.propTypes = { health: PropTypes.object.isRequired, endedAt: PropTypes.string, id: PropTypes.string };

export default function HistoryTab({ vendorId }) {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [historyType, setHistoryType] = useState({ label: 'All', value: 'all' });
  const [stripRuns, setStripRuns] = useState([]);
  const [open, setOpen] = useState({});

  async function getHistory(e) {
    const selectedType = e?.value?.length ? e : historyType;
    setHistoryType(selectedType);
    try {
      const res = await BZAPIService.getBugServiceHistory(vendorId, selectedType.value);
      setHistory(onlyConfigured(res));
      setIsLoading(false);
    } catch (err) {
      console.log(err);
      setIsLoading(false);
    }
  }

  useEffect(() => {
    setHistory([]);
    setIsLoading(true);
    setHistoryType({ label: 'All', value: 'all' });
    setOpen({});
    getHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  // The health strip reads the last 30 runs on its own; the table's filter doesn't change it.
  useEffect(() => {
    let cancelled = false;
    setStripRuns([]);
    BZAPIService.getBugServiceHistory(vendorId, 'all', STRIP_RUNS)
      .then(res => {
        if (!cancelled) setStripRuns(onlyConfigured(res).reverse());
      })
      .catch(err => {
        if (!cancelled) console.log(err);
      });
    return () => {
      cancelled = true;
    };
  }, [vendorId]);

  const showStrip = useMemo(() => stripRuns.some(r => healthOf(r)), [stripRuns]);
  const anyHealth = history.some(h => healthOf(h));

  const toggle = rowKey => setOpen(o => ({ ...o, [rowKey]: !o[rowKey] }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
      {!isLoading && showStrip && <HealthStrip runs={stripRuns} />}

      <div className="bz-card">
        {isLoading ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5">
            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '2rem', height: '2rem' }}>
              <span className="sr-only">Loading...</span>
            </div>
            <p className="bz-muted mb-0">Loading history...</p>
          </div>
        ) : (
          <>
            <div className="rh-card-head" style={{ alignItems: 'flex-end' }}>
              <div>
                <h3 className="bz-h3">Bug Service Execution History</h3>
                <div className="bz-muted" style={{ fontSize: 'var(--fs-sm)', marginTop: 2 }}>
                  Displaying up to the last 10 executions
                  {anyHealth ? ' · expand a run to see what it checked' : ''}
                </div>
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220, margin: 0 }}>
                <span style={{ fontWeight: 500, fontSize: 'var(--fs-sm)', color: 'var(--ink-soft)' }}>
                  Choose data to display:
                </span>
                <Select options={FILTER_OPTIONS} value={historyType} onChange={e => getHistory(e)} />
              </label>
            </div>

            {!isEmpty(history) ? (
              <table className="bz-table rh-history" aria-label="Bug service runs">
                <colgroup>
                  <col className="rh-history__exp" />
                  <col className="rh-history__ended" />
                  <col className="rh-history__health" />
                  <col className="rh-history__errors" />
                  {COUNT_COLUMNS.map(c => (
                    <col key={c.key} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">
                      <span className="rh-sr">Expand</span>
                    </th>
                    <th scope="col">Ended</th>
                    <th scope="col">Health</th>
                    <th scope="col">Errors</th>
                    {COUNT_COLUMNS.map(c => (
                      <th scope="col" key={c.key} className="rh-num">
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.map((entry, index) => {
                    const rowKey = entry.id ?? index;
                    const health = healthOf(entry);
                    const isOpen = Boolean(health && open[rowKey]);
                    const ended = formatDate(entry.endedAt);
                    const error = firstError(entry);
                    const detailId = `run-detail-${rowKey}`;
                    return (
                      <Fragment key={rowKey}>
                        <tr className={isOpen ? 'is-open' : undefined}>
                          <td>
                            {health && (
                              <button
                                type="button"
                                className="rh-exp"
                                aria-expanded={isOpen}
                                aria-controls={isOpen ? detailId : undefined}
                                aria-label={`Show what the ${ended} run checked`}
                                onClick={() => toggle(rowKey)}
                              >
                                <Chevron open={isOpen} />
                              </button>
                            )}
                          </td>
                          <td>
                            <span className="bz-mono rh-history__ended-time">{ended}</span>
                            {entry.vendorData?.syncOnly === true && (
                              <span className="bz-pill rh-history__sync">Sync only</span>
                            )}
                          </td>
                          <td>
                            <HealthCell health={health} />
                          </td>
                          <td>
                            {error ? (
                              <span className="rh-history__error" title={error}>
                                {error}
                              </span>
                            ) : (
                              '-'
                            )}
                          </td>
                          {COUNT_COLUMNS.map(c => (
                            <td key={c.key} className="rh-num">
                              {countValue(entry.vendorData?.[c.key])}
                            </td>
                          ))}
                        </tr>
                        {isOpen && (
                          <tr className="rh-history__detail">
                            <td colSpan={COLUMN_COUNT}>
                              <RunDetail health={health} endedAt={entry.endedAt} id={detailId} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: 16 }}>
                <strong>No execution logs found</strong>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

HistoryTab.propTypes = {
  vendorId: PropTypes.string
};
